import React from "react";
import { motion } from "framer-motion";
import { MapPin, Train, Plane, ArrowRight } from "lucide-react";
import PageLayout from "@/components/PageLayout";
import { ROUTES } from "@/lib/routes";
import { buildWhatsAppLink, SITE } from "@/lib/site";

const TIMES = [
  { place: "Ottapalam, Cherpulassery, Pattambi", time: "20 to 30 minutes" },
  { place: "Thrissur", time: "About 1 hour" },
  { place: "Perinthalmanna, Guruvayur", time: "About 1 hour 15 minutes" },
  { place: "Palakkad town", time: "About 1 hour 30 minutes" },
  { place: "Calicut (Kozhikode) airport", time: "About 2 hours 30 minutes" },
  { place: "Kochi airport", time: "About 2 hours 30 minutes" },
];

const REASONS = [
  {
    title: "A villa of your own",
    body: "Not a hospital room. A standalone cottage in quiet green grounds, with space for your mother or husband.",
  },
  {
    title: "Doctor-led care",
    body: "Dr. Nimisha, our treating physician, sees you every 2 days and sets your treatments.",
  },
  {
    title: "Our own medicines",
    body: "Oils and medicines come from Kakkanat Ayurveda Asramam, our family's GMP-certified pharmacy, part of an Ayurveda tradition since 1946.",
  },
  {
    title: "Real Kerala food",
    body: "Seven fresh meals a day, cooked for a new mother's recovery and milk.",
  },
  {
    title: "Only 3 families at a time",
    body: "You are never one of fifty.",
  },
];

const fade = {
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export const LocationPage = () => {
  return (
    <PageLayout
      title={ROUTES.location.title}
      description={ROUTES.location.description}
      testId="location-page"
    >
      {/* Hero */}
      <section className="pb-16 md:pb-24 bg-grain">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div {...fade}>
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-sage font-medium">
              <MapPin size={14} strokeWidth={1.5} />
              <span>Near Palakkad · Thrissur · Ottapalam</span>
            </div>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-charcoal leading-[1.08] tracking-tight">
              Ayurvedic Postnatal Care near{" "}
              <span className="italic text-sage-700">Palakkad</span> and{" "}
              <span className="italic text-sage-700">Thrissur</span>.
            </h1>
            <p className="mt-6 text-base md:text-lg text-charcoal/70 leading-relaxed max-w-3xl">
              Looking for Prasava Raksha near you? Kakkanat is in Paruthipra,
              Shoranur, right between Palakkad and Thrissur districts. Most
              families reach us in under an hour, and Shoranur Junction railway
              station is minutes away.
            </p>
            <p className="mt-5 text-base md:text-lg text-charcoal/70 leading-relaxed max-w-3xl">
              We are a small, doctor-led postnatal retreat with only three
              private villas. Since 2015, mothers from across Kerala and the
              Gulf have come here for the traditional 42 days of care.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <a
                href={buildWhatsAppLink("availability")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-charcoal text-ivory px-6 py-3.5 rounded-full text-sm font-medium hover:bg-sage transition-colors"
                data-testid="location-whatsapp-cta"
              >
                WhatsApp +91 90616 22255
                <ArrowRight size={15} />
              </a>
              <a
                href="/#pricing"
                className="inline-flex items-center gap-2 border border-sage/40 text-charcoal px-6 py-3.5 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
                data-testid="location-pricing-link"
              >
                See all packages
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Distance table */}
      <section className="py-16 md:py-24 bg-sand/50 border-y border-sage/15">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-sage font-medium">
              <Train size={14} strokeWidth={1.5} />
              <span>How far are we?</span>
            </div>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-charcoal leading-tight tracking-tight">
              Approximate road times to Kakkanat.
            </h2>
          </motion.div>
          <motion.div {...fade} className="mt-10 overflow-hidden rounded-2xl border border-sage/20 bg-white">
            <table className="w-full text-left" data-testid="location-distance-table">
              <tbody>
                {TIMES.map((row, i) => (
                  <tr
                    key={row.place}
                    className={i !== TIMES.length - 1 ? "border-b border-sage/15" : ""}
                  >
                    <th
                      scope="row"
                      className="px-5 md:px-7 py-4 md:py-5 font-serif text-base md:text-lg text-charcoal font-normal"
                    >
                      {row.place}
                    </th>
                    <td className="px-5 md:px-7 py-4 md:py-5 text-sm md:text-base text-charcoal/70 text-right">
                      {row.time}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
          <motion.p {...fade} className="mt-8 text-base text-charcoal/70 leading-relaxed max-w-3xl">
            <Plane size={14} strokeWidth={1.5} className="inline mb-0.5 text-sage" />
            {"  "}Coming from the Gulf? Land at Kochi or Calicut and we will
            guide your family on the drive. Many NRI mothers book before they
            fly home for delivery.
          </motion.p>
        </div>
      </section>

      {/* Why mothers travel */}
      <section className="py-16 md:py-28 bg-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Why mothers travel to Kakkanat
            </div>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-charcoal leading-tight tracking-tight">
              A small retreat, held with care.
            </h2>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-2 gap-6 md:gap-8">
            {REASONS.map((r, i) => (
              <motion.article
                key={r.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="p-7 md:p-8 rounded-3xl bg-white border border-sage/15"
                data-testid={`location-reason-${i + 1}`}
              >
                <div className="font-serif text-xl md:text-2xl text-charcoal">
                  {r.title}
                </div>
                <p className="mt-3 text-base text-charcoal/70 leading-relaxed">
                  {r.body}
                </p>
              </motion.article>
            ))}
          </div>
          <motion.div {...fade} className="mt-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-6 md:p-8 rounded-2xl bg-sand/60 border border-sage/15">
            <div>
              <div className="font-serif text-xl text-charcoal">Our packages</div>
              <p className="text-sm text-charcoal/65 mt-1">
                7, 14, 21, 28 and 42 day stays, from ₹15,000.
              </p>
            </div>
            <a
              href="/#pricing"
              className="inline-flex items-center justify-center gap-2 bg-sage text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors"
              data-testid="location-see-packages"
            >
              See all packages
              <ArrowRight size={15} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Find us */}
      <section className="py-16 md:py-24 bg-sand/50 border-t border-sage/15">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div {...fade} className="max-w-2xl">
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Find us
            </div>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl text-charcoal leading-tight tracking-tight">
              Paruthipra, Shoranur, Palakkad 679121.
            </h2>
            <p className="mt-4 text-base text-charcoal/70 leading-relaxed">
              Kakkanat Postnatal Care, Paruthipra, Post Office Road, Shoranur,
              Palakkad, Kerala 679121.
            </p>
          </motion.div>
          <motion.div {...fade} className="mt-8 rounded-2xl overflow-hidden border border-sage/20">
            <iframe
              src="https://www.google.com/maps?q=Kakkanat%20Ayurveda%20Postnatal%20care%20centre%20%2F%20Prasavaraksha%20centre%2C%20Paruthipra%2C%20Post%20Office%20Rd%2C%20Shoranur%2C%20Kerala%20679121&output=embed"
              title="Map showing Kakkanat Ayurveda Postnatal Care Centre"
              className="block w-full h-72 md:h-96 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
              data-testid="location-map"
            />
          </motion.div>
          <motion.div {...fade} className="mt-8 flex flex-wrap gap-4">
            <a
              href="https://maps.app.goo.gl/i3WyDCZjckCY6SGM8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-sage/40 text-charcoal px-6 py-3 rounded-full text-sm font-medium hover:bg-sage hover:text-ivory hover:border-sage transition-colors"
              data-testid="location-directions"
            >
              <MapPin size={15} strokeWidth={1.75} />
              Get Directions
            </a>
            <a
              href={buildWhatsAppLink("default")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-sage text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors"
              data-testid="location-whatsapp-bottom"
            >
              WhatsApp {SITE.whatsappDisplay}
            </a>
          </motion.div>
          <p className="mt-5 text-sm text-charcoal/55">9am to 8pm, every day.</p>
        </div>
      </section>
    </PageLayout>
  );
};

export default LocationPage;
